import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

import type { GridSelection } from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'
import { buildColumnRangeKeys } from 'modules/shift_management/utils/TimeGridTable/selection/builds'
type HeaderDragAxis = 'row' | 'column'
type HeaderPressState = 'idle' | 'pending' | 'dragging'
type SelectionModifiers = { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean }

interface HeaderPress {
  axis: HeaderDragAxis
  pressedIndex: number
  dragAnchor: number | null
  modifiers: SelectionModifiers
  state: HeaderPressState
  isPrimaryButtonPressed: boolean
}

interface GridSelectionState {
  selection: GridSelection
  rowAnchor: number | null
  columnAnchor: number | null
  suppressedHeaderClickAxis: HeaderDragAxis | null
  headerPress: HeaderPress | null
}

interface UseHeaderDragSelectionParams {
  headerPress: HeaderPress | null
  setGridState: React.Dispatch<React.SetStateAction<GridSelectionState>>
}

function useHeaderDragSelection({ headerPress, setGridState }: UseHeaderDragSelectionParams): {
  handleHeaderDragStart: (params: {
    axis: HeaderDragAxis
    index: number
    modifiers: SelectionModifiers
    isPrimaryButtonPressed?: boolean
  }) => void
} {
  const updateHeaderRangeSelection = useUnsafeCallback(
    (index: number) => {
      setGridState((prev) => {
        const currentHeaderPress = prev.headerPress
        if (!currentHeaderPress) return prev

        if (currentHeaderPress.state === 'pending') {
          if (index === currentHeaderPress.pressedIndex) return prev

          const dragAnchor =
            currentHeaderPress.axis === 'column'
              ? currentHeaderPress.modifiers.shiftKey
                ? (prev.columnAnchor ?? currentHeaderPress.pressedIndex)
                : currentHeaderPress.pressedIndex
              : currentHeaderPress.modifiers.shiftKey
                ? (prev.rowAnchor ?? currentHeaderPress.pressedIndex)
                : currentHeaderPress.pressedIndex

          const start = Math.min(dragAnchor, index)
          const end = Math.max(dragAnchor, index)

          return {
            ...prev,
            selection:
              currentHeaderPress.axis === 'column'
                ? { kind: 'columnRange', keys: buildColumnRangeKeys(start, end) }
                : { kind: 'rowRange', startIndex: start, endIndex: end },
            rowAnchor: currentHeaderPress.axis === 'row' ? dragAnchor : prev.rowAnchor,
            columnAnchor: currentHeaderPress.axis === 'column' ? dragAnchor : prev.columnAnchor,
            suppressedHeaderClickAxis: currentHeaderPress.axis,
            headerPress: {
              ...currentHeaderPress,
              dragAnchor,
              state: 'dragging',
            },
          }
        }

        if (currentHeaderPress.state !== 'dragging' || currentHeaderPress.dragAnchor === null) {
          return prev
        }

        const start = Math.min(currentHeaderPress.dragAnchor, index)
        const end = Math.max(currentHeaderPress.dragAnchor, index)

        return {
          ...prev,
          selection:
            currentHeaderPress.axis === 'column'
              ? { kind: 'columnRange', keys: buildColumnRangeKeys(start, end) }
              : { kind: 'rowRange', startIndex: start, endIndex: end },
        }
      })
    },
    [setGridState]
  )

  const handleHeaderDragStart = useUnsafeCallback(
    ({
      axis,
      index,
      modifiers,
      isPrimaryButtonPressed = true,
    }: {
      axis: HeaderDragAxis
      index: number
      modifiers: SelectionModifiers
      isPrimaryButtonPressed?: boolean
    }) => {
      setGridState((prev) => ({
        ...prev,
        suppressedHeaderClickAxis: null,
        headerPress: {
          axis,
          pressedIndex: index,
          dragAnchor: null,
          modifiers,
          state: 'pending',
          isPrimaryButtonPressed,
        },
      }))
    },
    [setGridState]
  )

  const handleHeaderDragEnd = useUnsafeCallback(() => {
    setGridState((prev) => ({
      ...prev,
      headerPress: null,
    }))
  }, [setGridState])

  useUnsafeEffect(() => {
    if (!headerPress) return

    const getIndexFromMouseEvent = (event: MouseEvent): number | null => {
      const axis = headerPress.axis

      const element = document.elementFromPoint(event.clientX, event.clientY)
      const selector =
        axis === 'column' ? '[data-time-grid-column-index]' : '[data-time-grid-row-index]'
      const dataKey = axis === 'column' ? 'timeGridColumnIndex' : 'timeGridRowIndex'
      const header = element?.closest<HTMLElement>(selector)
      if (!header) return null

      const headerIndex = header.dataset[dataKey]
      if (!headerIndex) return null

      const parsedHeaderIndex = Number(headerIndex)
      return Number.isNaN(parsedHeaderIndex) ? null : parsedHeaderIndex
    }

    const onMouseMove = (event: MouseEvent) => {
      if ((event.buttons & 1) === 0) {
        handleHeaderDragEnd()
        return
      }

      const index = getIndexFromMouseEvent(event)
      if (index === null) return
      updateHeaderRangeSelection(index)
    }

    const onMouseUp = () => {
      handleHeaderDragEnd()
    }

    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)

    return () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }
  }, [handleHeaderDragEnd, headerPress, updateHeaderRangeSelection])

  return {
    handleHeaderDragStart,
  }
}

export { useHeaderDragSelection }
export type { GridSelectionState, HeaderDragAxis, HeaderPress, SelectionModifiers }
